import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, SlidersHorizontal, Bell, Palette, CreditCard,
  Database, Shield, Save, Plus, X, ChevronRight, AlertTriangle,
  Download, Trash2, LogOut, Eye, EyeOff, Crown
} from 'lucide-react';
import {
  getUserProfile, saveUserProfile,
  getTrades, deleteTrade
} from '../services/firestoreService';
import { getUserSubscription, cancelSubscription } from '../subscriptionUtils';
import { getSubscription } from '../services/firestoreService';
import { auth } from '../firebase';
import {
  updateProfile, updatePassword,
  reauthenticateWithCredential, EmailAuthProvider
} from 'firebase/auth';
import { useTheme } from '../context/ThemeContext';
import './Settings.css';

// ── Constants ────────────────────────────────────────────────────────────────
const MENU_ITEMS = [
  { id: 'profile',  label: 'Profile',              icon: User            },
  { id: 'prefs',    label: 'Trading Preferences',  icon: SlidersHorizontal },
  { id: 'notifs',   label: 'Notifications',         icon: Bell            },
  { id: 'appear',   label: 'Appearance',            icon: Palette         },
  { id: 'billing',  label: 'Account & Billing',     icon: CreditCard      },
  { id: 'data',     label: 'Data Management',       icon: Database        },
  { id: 'security', label: 'Security',              icon: Shield          },
];

const BROKERS   = ['Zerodha', 'Upstox', 'Angel One', 'Groww', 'ICICI Direct', 'HDFC Securities', 'Other'];
const MARKETS   = ['Equity (NSE/BSE)', 'F&O (Futures & Options)', 'Commodity (MCX)', 'Currency', 'Crypto', 'US Stocks'];
const STYLES    = ['Scalping', 'Intraday', 'Swing', 'Positional', 'Investor'];
const EXPERIENCES = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const YEARS     = Array.from({ length: 17 }, (_, i) => String(2010 + i));
const DATE_FMTS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];

const PLAN_FEATURES = {
  free:    ['10 trades/month', 'Basic P&L tracking', 'Trade journal'],
  pro:     ['Unlimited trades', 'AI Insights', 'Psychology Tracker', 'Risk Manager', 'Reports'],
  elite:   ['Everything in Pro', 'AI Assistant', 'CSV Broker Import', 'Priority support', 'Weekly AI reports'],
};

const PLAN_PRICES = { free: 'Free', pro: '₹999/mo', elite: '₹1999/mo' };

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const initials = name => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name[0].toUpperCase();
};

// ── Default state factories ───────────────────────────────────────────────────
const defaultProfile = (user) => ({
  displayName: user?.name || '', phone: '',
  tradingSince: '2020', experience: 'Intermediate', bio: '', city: '',
});

const defaultPrefs = () => ({
  capital: '', markets: [], broker: 'Zerodha', style: 'Intraday',
  setupTags: ['Breakout', 'Reversal', 'Momentum', 'Gap Up'],
  emotionTags: ['Calm', 'FOMO', 'Greedy', 'Fearful'],
  brokerage: '', riskPerTrade: '2',
});

const defaultNotifs = () => ({
  dailyReminder: false, reminderTime: '09:00',
  dailySummary: false,  summaryTime: '16:00',
  riskAlert: true, winStreak: false,
  lossStreak: true, weeklyReport: false,
  announcements: true,
});

const defaultAppear = () => ({
  language: 'english', dateFormat: 'DD/MM/YYYY',
  numberFormat: 'indian', sidebarDefault: 'expanded',
});

// ════════════════════════════════════════════════════════════════════════════
const Settings = ({ currentUser, onNavigate }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [section, setSection] = useState('profile');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);

  // Section states
  const [profile, setProfile]   = useState(() => defaultProfile(currentUser));
  const [prefs, setPrefs]       = useState(defaultPrefs);
  const [notifs, setNotifs]     = useState(defaultNotifs);
  const [appear, setAppear]     = useState(defaultAppear);

  // Derived / billing
  const [sub, setSub]           = useState(null);
  const [tradeCount, setTradeCount] = useState(0);
  const [trades, setTrades]     = useState([]);

  // Tag inputs
  const [newSetup, setNewSetup]     = useState('');
  const [newEmotion, setNewEmotion] = useState('');

  // Security
  const [curPwd, setCurPwd]         = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd]       = useState(false);

  // Data management modals
  const [clearConfirm, setClearConfirm]   = useState('');
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDeleteAcct, setShowDeleteAcct] = useState(false);
  const [deleteAcctPwd, setDeleteAcctPwd] = useState('');

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Load data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.id) return;
    const load = async () => {
      try {
        const [p, s, ts] = await Promise.all([
          getUserProfile(currentUser.id),
          getSubscription(currentUser.id),
          getTrades(currentUser.id),
        ]);
        if (p) {
          if (p.profile)       setProfile(prev => ({ ...defaultProfile(currentUser), ...p.profile }));
          if (p.tradingPrefs)  setPrefs(prev  => ({ ...defaultPrefs(), ...p.tradingPrefs }));
          if (p.notifications) setNotifs(prev => ({ ...defaultNotifs(), ...p.notifications }));
          if (p.appearance)    setAppear(prev => ({ ...defaultAppear(), ...p.appearance }));
        }
        setSub(s);
        setTradeCount(ts.length);
        setTrades(ts);
      } catch { /* profile may not exist yet */ }
      setLoading(false);
    };
    load();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save helpers ──────────────────────────────────────────────────────────
  const withSave = async (fn) => {
    setSaving(true);
    try { await fn(); showToast('Saved successfully'); }
    catch (e) { showToast(e.message || 'Save failed', 'error'); }
    setSaving(false);
  };

  const saveProfile = () => withSave(async () => {
    await saveUserProfile(currentUser.id, { profile });
    if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: profile.displayName });
  });

  const savePrefs = () => withSave(() =>
    saveUserProfile(currentUser.id, { tradingPrefs: prefs })
  );

  const saveNotifs = () => withSave(() =>
    saveUserProfile(currentUser.id, { notifications: notifs })
  );

  const saveAppear = () => withSave(() =>
    saveUserProfile(currentUser.id, { appearance: appear })
  );

  const changePassword = () => withSave(async () => {
    if (!newPwd || newPwd !== confirmPwd) throw new Error('Passwords do not match');
    if (newPwd.length < 6) throw new Error('Password must be at least 6 characters');
    const credential = EmailAuthProvider.credential(currentUser.email, curPwd);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPwd);
    setCurPwd(''); setNewPwd(''); setConfirmPwd('');
    showToast('Password changed successfully');
  });

  const handleCancelSub = () => withSave(async () => {
    const result = await cancelSubscription(currentUser.id);
    if (!result.success) throw new Error(result.message);
    const updated = await getSubscription(currentUser.id);
    setSub(updated);
    showToast(result.message);
  });

  // ── Export helpers ────────────────────────────────────────────────────────
  const exportCSV = (headers, rows, filename) => {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportAllTrades = () => {
    exportCSV(
      ['Date', 'Symbol', 'Type', 'Entry', 'Exit', 'Qty', 'P&L', 'Emotion', 'Setup', 'Notes'],
      trades.map(t => [t.date, t.stock || t.symbol || '', t.type || '', t.entry || '', t.exit || '', t.quantity || '', t.pnl || 0, t.emotion || '', t.setup || '', t.notes || '']),
      `tradefix-trades-${new Date().toISOString().split('T')[0]}.csv`
    );
  };

  const exportJSON = async () => {
    const p = await getUserProfile(currentUser.id);
    const data = { profile: p, trades, subscription: sub, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `tradefix-data-${new Date().toISOString().split('T')[0]}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearTrades = async () => {
    if (clearConfirm !== 'DELETE') return showToast('Type DELETE to confirm', 'error');
    setSaving(true);
    try {
      await Promise.all(trades.map(t => deleteTrade(currentUser.id, t.id)));
      setTrades([]); setTradeCount(0);
      setShowClearModal(false); setClearConfirm('');
      showToast('All trades cleared');
    } catch { showToast('Failed to clear trades', 'error'); }
    setSaving(false);
  };

  // ── Tag helpers ───────────────────────────────────────────────────────────
  const addTag = (field, val, setVal) => {
    const v = val.trim();
    if (!v) return;
    setPrefs(p => ({ ...p, [field]: [...(p[field] || []), v] }));
    setVal('');
  };

  const removeTag = (field, idx) =>
    setPrefs(p => ({ ...p, [field]: p[field].filter((_, i) => i !== idx) }));

  // ── Shared UI primitives ──────────────────────────────────────────────────
  const Toggle = ({ checked, onChange }) => (
    <button
      role="switch" aria-checked={checked}
      className={`st-toggle ${checked ? 'on' : ''}`}
      onClick={() => onChange(!checked)}
    />
  );

  const SectionHead = ({ title }) => (
    <div className="st-section-head">{title}</div>
  );

  const FormRow = ({ label, hint, children }) => (
    <div className="st-form-row">
      <div className="st-form-label">
        <span>{label}</span>
        {hint && <span className="st-form-hint">{hint}</span>}
      </div>
      <div className="st-form-field">{children}</div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // SECTION RENDERERS
  // ══════════════════════════════════════════════════════════════════════════

  // ── 1. Profile ────────────────────────────────────────────────────────────
  const renderProfile = () => (
    <div className="st-pane">
      <h2 className="st-pane-title">Profile</h2>

      {/* Avatar */}
      <div className="st-avatar-row">
        <div className="st-avatar">
          {currentUser?.photoURL
            ? <img src={currentUser.photoURL} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : initials(profile.displayName || currentUser?.name)}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{profile.displayName || 'Your Name'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{currentUser?.email}</div>
        </div>
      </div>

      <SectionHead title="Personal Info" />

      <FormRow label="Full Name">
        <input className="st-input" value={profile.displayName} onChange={e => setProfile(p => ({ ...p, displayName: e.target.value }))} placeholder="Your full name" />
      </FormRow>

      <FormRow label="Email">
        <input className="st-input" value={currentUser?.email || ''} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
      </FormRow>

      <FormRow label="Phone Number">
        <input className="st-input" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210" />
      </FormRow>

      <FormRow label="Trading Since">
        <select className="st-input" value={profile.tradingSince} onChange={e => setProfile(p => ({ ...p, tradingSince: e.target.value }))}>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </FormRow>

      <FormRow label="Trading City">
        <input className="st-input" value={profile.city} onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} placeholder="Mumbai, Delhi…" />
      </FormRow>

      <SectionHead title="Experience Level" />

      <div className="st-radio-group">
        {EXPERIENCES.map(exp => (
          <label key={exp} className="st-radio-label">
            <input type="radio" name="experience" value={exp} checked={profile.experience === exp} onChange={() => setProfile(p => ({ ...p, experience: exp }))} />
            {exp}
          </label>
        ))}
      </div>

      <SectionHead title="Bio" />
      <textarea
        className="st-input st-textarea"
        value={profile.bio}
        onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
        placeholder="Tell us about your trading journey…"
        rows={3}
      />

      <button className="st-save-btn" onClick={saveProfile} disabled={saving}>
        <Save size={14} /> {saving ? 'Saving…' : 'Save Profile'}
      </button>
    </div>
  );

  // ── 2. Trading Preferences ────────────────────────────────────────────────
  const renderPrefs = () => (
    <div className="st-pane">
      <h2 className="st-pane-title">Trading Preferences</h2>

      <SectionHead title="Capital & Risk" />

      <FormRow label="Trading Capital (₹)" hint="Used for P&L % calculation">
        <input className="st-input" type="number" value={prefs.capital} onChange={e => setPrefs(p => ({ ...p, capital: e.target.value }))} placeholder="e.g. 500000" />
      </FormRow>

      <FormRow label="Brokerage per Trade (₹)" hint="For accurate net P&L">
        <input className="st-input" type="number" value={prefs.brokerage} onChange={e => setPrefs(p => ({ ...p, brokerage: e.target.value }))} placeholder="e.g. 20" />
      </FormRow>

      <FormRow label="Risk per Trade (% of capital)">
        <input className="st-input" type="number" value={prefs.riskPerTrade} onChange={e => setPrefs(p => ({ ...p, riskPerTrade: e.target.value }))} placeholder="e.g. 2" />
      </FormRow>

      <SectionHead title="Preferred Markets" />
      <div className="st-check-group">
        {MARKETS.map(m => (
          <label key={m} className="st-check-label">
            <input
              type="checkbox"
              checked={(prefs.markets || []).includes(m)}
              onChange={e => setPrefs(p => ({
                ...p,
                markets: e.target.checked ? [...(p.markets || []), m] : (p.markets || []).filter(x => x !== m)
              }))}
            />
            {m}
          </label>
        ))}
      </div>

      <SectionHead title="Default Broker" />
      <select className="st-input" style={{ maxWidth: 240 }} value={prefs.broker} onChange={e => setPrefs(p => ({ ...p, broker: e.target.value }))}>
        {BROKERS.map(b => <option key={b} value={b}>{b}</option>)}
      </select>

      <SectionHead title="Trading Style" />
      <div className="st-radio-group">
        {STYLES.map(s => (
          <label key={s} className="st-radio-label">
            <input type="radio" name="style" value={s} checked={prefs.style === s} onChange={() => setPrefs(p => ({ ...p, style: s }))} />
            {s}
          </label>
        ))}
      </div>

      <SectionHead title="Default Setup Tags" />
      <div className="st-chips">
        {(prefs.setupTags || []).map((tag, i) => (
          <span key={i} className="st-chip">
            {tag}
            <button className="st-chip-remove" onClick={() => removeTag('setupTags', i)}><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="st-tag-input">
        <input className="st-input" value={newSetup} onChange={e => setNewSetup(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag('setupTags', newSetup, setNewSetup)} placeholder="Add setup tag…" style={{ maxWidth: 200 }} />
        <button className="st-add-btn" onClick={() => addTag('setupTags', newSetup, setNewSetup)}><Plus size={14} /> Add</button>
      </div>

      <SectionHead title="Default Emotion Tags" />
      <div className="st-chips">
        {(prefs.emotionTags || []).map((tag, i) => (
          <span key={i} className="st-chip">
            {tag}
            <button className="st-chip-remove" onClick={() => removeTag('emotionTags', i)}><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="st-tag-input">
        <input className="st-input" value={newEmotion} onChange={e => setNewEmotion(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag('emotionTags', newEmotion, setNewEmotion)} placeholder="Add emotion tag…" style={{ maxWidth: 200 }} />
        <button className="st-add-btn" onClick={() => addTag('emotionTags', newEmotion, setNewEmotion)}><Plus size={14} /> Add</button>
      </div>

      <button className="st-save-btn" onClick={savePrefs} disabled={saving}>
        <Save size={14} /> {saving ? 'Saving…' : 'Save Preferences'}
      </button>
    </div>
  );

  // ── 3. Notifications ──────────────────────────────────────────────────────
  const NotifRow = ({ label, hint, field, showTime, timeField }) => (
    <div className="st-notif-row">
      <div className="st-notif-info">
        <div className="st-notif-label">{label}</div>
        {hint && <div className="st-notif-hint">{hint}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {showTime && notifs[field] && (
          <input
            type="time"
            className="st-input"
            value={notifs[timeField]}
            onChange={e => setNotifs(n => ({ ...n, [timeField]: e.target.value }))}
            style={{ width: 110 }}
          />
        )}
        <Toggle checked={notifs[field]} onChange={v => setNotifs(n => ({ ...n, [field]: v }))} />
      </div>
    </div>
  );

  const renderNotifs = () => (
    <div className="st-pane">
      <h2 className="st-pane-title">Notifications</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
        Notification preferences are saved locally. Push delivery depends on your device settings.
      </p>

      <SectionHead title="Daily Reminders" />
      <NotifRow label="Daily Trading Reminder"   hint="Remind you to log your trades" field="dailyReminder" showTime timeField="reminderTime" />
      <NotifRow label="Daily P&L Summary"         hint="End-of-day performance summary" field="dailySummary" showTime timeField="summaryTime" />

      <SectionHead title="Risk Alerts" />
      <NotifRow label="Risk Limit Breach Alert"   hint="Alert when daily loss limit is hit"            field="riskAlert"    />
      <NotifRow label="Win Streak Notification"   hint="Notify on 3+ consecutive wins"                 field="winStreak"    />
      <NotifRow label="Loss Streak Warning"        hint="Warn on 3+ consecutive losses"                 field="lossStreak"   />

      <SectionHead title="Reports" />
      <NotifRow label="Weekly Report Email"        hint="Summary every Monday morning"                  field="weeklyReport" />
      <NotifRow label="New Feature Announcements"  hint="Be first to know about new features"           field="announcements" />

      <button className="st-save-btn" onClick={saveNotifs} disabled={saving}>
        <Save size={14} /> {saving ? 'Saving…' : 'Save Notifications'}
      </button>
    </div>
  );

  // ── 4. Appearance ─────────────────────────────────────────────────────────
  const renderAppear = () => (
    <div className="st-pane">
      <h2 className="st-pane-title">Appearance</h2>

      <SectionHead title="Theme" />
      <div className="st-theme-cards">
        <button className={`st-theme-card st-theme-dark ${theme === 'dark' ? 'active' : ''}`} onClick={() => theme !== 'dark' && toggleTheme()}>
          <div className="st-theme-preview st-preview-dark">
            <div className="st-preview-bar" /><div className="st-preview-card" /><div className="st-preview-card" />
          </div>
          <span>Dark Mode</span>
          {theme === 'dark' && <span className="st-theme-badge">Active</span>}
        </button>
        <button className={`st-theme-card st-theme-light ${theme === 'light' ? 'active' : ''}`} onClick={() => theme !== 'light' && toggleTheme()}>
          <div className="st-theme-preview st-preview-light">
            <div className="st-preview-bar" /><div className="st-preview-card" /><div className="st-preview-card" />
          </div>
          <span>Light Mode</span>
          {theme === 'light' && <span className="st-theme-badge">Active</span>}
        </button>
      </div>

      <SectionHead title="Language" />
      <div className="st-radio-group">
        {[['english','English'],['hindi','Hindi'],['hinglish','Hinglish (Hindi + English)']].map(([v, l]) => (
          <label key={v} className="st-radio-label">
            <input type="radio" name="language" value={v} checked={appear.language === v} onChange={() => setAppear(a => ({ ...a, language: v }))} />
            {l}
          </label>
        ))}
      </div>

      <SectionHead title="Date Format" />
      <select className="st-input" style={{ maxWidth: 220 }} value={appear.dateFormat} onChange={e => setAppear(a => ({ ...a, dateFormat: e.target.value }))}>
        {DATE_FMTS.map(f => <option key={f} value={f}>{f}</option>)}
      </select>

      <SectionHead title="Number Format" />
      <div className="st-radio-group">
        <label className="st-radio-label">
          <input type="radio" name="numfmt" checked={appear.numberFormat === 'indian'} onChange={() => setAppear(a => ({ ...a, numberFormat: 'indian' }))} />
          Indian (1,00,000)
        </label>
        <label className="st-radio-label">
          <input type="radio" name="numfmt" checked={appear.numberFormat === 'international'} onChange={() => setAppear(a => ({ ...a, numberFormat: 'international' }))} />
          International (100,000)
        </label>
      </div>

      <SectionHead title="Sidebar Default" />
      <div className="st-radio-group">
        <label className="st-radio-label">
          <input type="radio" name="sidebar" checked={appear.sidebarDefault === 'expanded'} onChange={() => setAppear(a => ({ ...a, sidebarDefault: 'expanded' }))} />
          Expanded
        </label>
        <label className="st-radio-label">
          <input type="radio" name="sidebar" checked={appear.sidebarDefault === 'collapsed'} onChange={() => setAppear(a => ({ ...a, sidebarDefault: 'collapsed' }))} />
          Collapsed
        </label>
      </div>

      <button className="st-save-btn" onClick={saveAppear} disabled={saving}>
        <Save size={14} /> {saving ? 'Saving…' : 'Save Appearance'}
      </button>
    </div>
  );

  // ── 5. Account & Billing ──────────────────────────────────────────────────
  const renderBilling = () => {
    const planId   = sub?.planId || 'free';
    const planName = sub?.planName || 'Free';
    const isActive = sub?.status === 'active' && sub?.endDate && new Date(sub.endDate) >= new Date();
    const features = PLAN_FEATURES[planId] || PLAN_FEATURES.free;

    return (
      <div className="st-pane">
        <h2 className="st-pane-title">Account &amp; Billing</h2>

        {/* Current plan */}
        <SectionHead title="Current Plan" />
        <div className="st-plan-card">
          <div className="st-plan-top">
            <div>
              <div className="st-plan-name">{planName} Plan</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                {isActive ? `Active until ${fmtDate(sub.endDate)}` : 'No active subscription'}
              </div>
            </div>
            <span className={`st-plan-badge ${isActive ? 'active' : 'free'}`}>
              {isActive ? 'ACTIVE' : planId === 'free' ? 'FREE' : 'EXPIRED'}
            </span>
          </div>
          <ul className="st-plan-features">
            {features.map(f => <li key={f}><span className="st-check-icon">✓</span>{f}</li>)}
          </ul>
          <button
            className="st-save-btn"
            style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
            onClick={() => navigate('/pricing')}
          >
            <Crown size={14} /> View All Plans
          </button>
        </div>

        {/* Upgrade cards if free */}
        {!isActive && (
          <>
            <SectionHead title="Upgrade Your Plan" />
            <div className="st-upgrade-cards">
              {[
                { id: 'pro',   name: 'Pro',   price: '₹999/mo'  },
                { id: 'elite', name: 'Elite', price: '₹1999/mo' },
              ].map(plan => (
                <div key={plan.id} className="st-upgrade-card">
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{plan.name}</div>
                  <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 18, margin: '6px 0' }}>{plan.price}</div>
                  <ul className="st-plan-features" style={{ marginBottom: 14 }}>
                    {(PLAN_FEATURES[plan.id] || []).map(f => <li key={f}><span className="st-check-icon">✓</span>{f}</li>)}
                  </ul>
                  <button className="st-save-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/pricing')}>
                    <Crown size={14} /> Upgrade
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Danger zone — cancel */}
        {isActive && (
          <>
            <SectionHead title="Danger Zone" />
            <div className="st-danger-zone">
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Cancel Subscription</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                  You'll retain access until {fmtDate(sub?.endDate)}. No refund after 14 days.
                </div>
              </div>
              <button className="st-danger-btn" onClick={handleCancelSub} disabled={saving}>
                Cancel Plan
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // ── 6. Data Management ────────────────────────────────────────────────────
  const renderData = () => (
    <div className="st-pane">
      <h2 className="st-pane-title">Data Management</h2>

      <SectionHead title="Data Statistics" />
      <div className="st-data-stats">
        <div className="st-data-stat"><span className="st-data-stat-val">{tradeCount}</span><span className="st-data-stat-label">Trades</span></div>
        <div className="st-data-stat"><span className="st-data-stat-val">{tradeCount}</span><span className="st-data-stat-label">Journal Entries</span></div>
        <div className="st-data-stat"><span className="st-data-stat-val">{fmtDate(currentUser?.createdAt)}</span><span className="st-data-stat-label">Account Created</span></div>
      </div>

      <SectionHead title="Export Data" />
      <div className="st-export-btns">
        <button className="st-outline-btn" onClick={exportAllTrades}>
          <Download size={14} /> Export All Trades (CSV)
        </button>
        <button className="st-outline-btn" onClick={exportJSON}>
          <Download size={14} /> Export Full Account Data (JSON)
        </button>
        <button className="st-outline-btn" onClick={() => {
          const tmpl = 'Date,Symbol,Type,Entry,Exit,Qty,Emotion,Setup,Notes\n2026-01-01,NIFTY,BUY,22000,22200,50,Calm,Breakout,\n';
          const blob = new Blob([tmpl], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = 'tradefix-template.csv'; a.click();
          URL.revokeObjectURL(url);
        }}>
          <Download size={14} /> Download CSV Template
        </button>
      </div>

      <SectionHead title="Danger Zone" />

      {/* Clear trades */}
      <div className="st-danger-zone" style={{ marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Clear All Trades</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
            Permanently delete all {tradeCount} trades. This cannot be undone.
          </div>
        </div>
        <button className="st-danger-btn" onClick={() => setShowClearModal(true)} disabled={tradeCount === 0}>
          <Trash2 size={13} /> Clear Trades
        </button>
      </div>

      {/* Delete account */}
      <div className="st-danger-zone">
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Delete Account</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
            Permanently delete your account and all data. This cannot be undone.
          </div>
        </div>
        <button className="st-danger-btn" onClick={() => setShowDeleteAcct(true)}>
          <Trash2 size={13} /> Delete Account
        </button>
      </div>

      {/* Clear trades modal */}
      {showClearModal && (
        <div className="st-modal-overlay" onClick={() => setShowClearModal(false)}>
          <div className="st-modal" onClick={e => e.stopPropagation()}>
            <AlertTriangle size={32} style={{ color: 'var(--danger)', marginBottom: 12 }} />
            <h3 style={{ margin: '0 0 8px', color: 'var(--text-primary)' }}>Clear All Trades?</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 16px' }}>
              This will delete all {tradeCount} trades permanently. Type <strong>DELETE</strong> to confirm.
            </p>
            <input className="st-input" value={clearConfirm} onChange={e => setClearConfirm(e.target.value)} placeholder="Type DELETE" style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="st-danger-btn" onClick={handleClearTrades} disabled={saving || clearConfirm !== 'DELETE'} style={{ flex: 1 }}>
                {saving ? 'Deleting…' : 'Delete All Trades'}
              </button>
              <button className="st-outline-btn" onClick={() => { setShowClearModal(false); setClearConfirm(''); }} style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete account modal */}
      {showDeleteAcct && (
        <div className="st-modal-overlay" onClick={() => setShowDeleteAcct(false)}>
          <div className="st-modal" onClick={e => e.stopPropagation()}>
            <AlertTriangle size={32} style={{ color: 'var(--danger)', marginBottom: 12 }} />
            <h3 style={{ margin: '0 0 8px', color: 'var(--text-primary)' }}>Delete Account?</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 16px' }}>
              This will permanently delete your account and all data. Enter your password to confirm.
            </p>
            <input className="st-input" type="password" value={deleteAcctPwd} onChange={e => setDeleteAcctPwd(e.target.value)} placeholder="Current password" style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="st-danger-btn" style={{ flex: 1 }}
                onClick={async () => {
                  if (!deleteAcctPwd) return showToast('Enter your password', 'error');
                  setSaving(true);
                  try {
                    const credential = EmailAuthProvider.credential(currentUser.email, deleteAcctPwd);
                    await reauthenticateWithCredential(auth.currentUser, credential);
                    await auth.currentUser.delete();
                  } catch (e) { showToast(e.message || 'Delete failed', 'error'); }
                  setSaving(false);
                }}
                disabled={saving}
              >
                {saving ? 'Deleting…' : 'Delete My Account'}
              </button>
              <button className="st-outline-btn" onClick={() => { setShowDeleteAcct(false); setDeleteAcctPwd(''); }} style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── 7. Security ───────────────────────────────────────────────────────────
  const renderSecurity = () => {
    const isEmailUser = auth.currentUser?.providerData?.[0]?.providerId === 'password';
    const lastLogin = auth.currentUser?.metadata?.lastSignInTime;

    return (
      <div className="st-pane">
        <h2 className="st-pane-title">Security</h2>

        <SectionHead title="Account Activity" />
        <div className="st-info-list">
          <div className="st-info-row"><span className="st-info-label">Login Method</span><span className="st-info-val">{isEmailUser ? 'Email & Password' : 'Google'}</span></div>
          <div className="st-info-row"><span className="st-info-label">Last Login</span><span className="st-info-val">{lastLogin ? new Date(lastLogin).toLocaleString('en-IN') : '—'}</span></div>
          <div className="st-info-row"><span className="st-info-label">Email</span><span className="st-info-val">{currentUser?.email}</span></div>
          <div className="st-info-row"><span className="st-info-label">Account ID</span><span className="st-info-val" style={{ fontFamily: 'monospace', fontSize: 11 }}>{currentUser?.id?.slice(0, 16)}…</span></div>
        </div>

        {isEmailUser ? (
          <>
            <SectionHead title="Change Password" />
            <FormRow label="Current Password">
              <div className="st-pwd-wrap">
                <input className="st-input" type={showPwd ? 'text' : 'password'} value={curPwd} onChange={e => setCurPwd(e.target.value)} placeholder="Current password" />
                <button className="st-eye-btn" onClick={() => setShowPwd(s => !s)}>
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </FormRow>
            <FormRow label="New Password">
              <input className="st-input" type={showPwd ? 'text' : 'password'} value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Min. 6 characters" />
            </FormRow>
            <FormRow label="Confirm Password">
              <input className="st-input" type={showPwd ? 'text' : 'password'} value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} placeholder="Repeat new password" />
            </FormRow>
            <button className="st-save-btn" onClick={changePassword} disabled={saving || !curPwd || !newPwd || !confirmPwd}>
              <Shield size={14} /> {saving ? 'Updating…' : 'Change Password'}
            </button>
          </>
        ) : (
          <div className="st-info-box">
            You signed in with Google. Password management is handled by your Google account.
          </div>
        )}

        <SectionHead title="Two-Factor Authentication" />
        <div className="st-info-box" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Two-Factor Authentication</span>
          <span className="st-coming-soon">Coming Soon</span>
        </div>

        <SectionHead title="Sessions" />
        <div className="st-danger-zone">
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Sign Out All Devices</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
              Signs you out from all active sessions. You'll need to log in again.
            </div>
          </div>
          <button className="st-danger-btn" onClick={async () => {
            try {
              const { signOut } = await import('firebase/auth');
              await signOut(auth);
            } catch { showToast('Sign out failed', 'error'); }
          }}>
            <LogOut size={13} /> Sign Out All
          </button>
        </div>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────
  const RENDERERS = {
    profile: renderProfile, prefs: renderPrefs,
    notifs: renderNotifs,   appear: renderAppear,
    billing: renderBilling, data: renderData,
    security: renderSecurity,
  };

  return (
    <div className="st-page">
      {/* Left menu */}
      <nav className="st-menu">
        {MENU_ITEMS.map(item => (
          <button
            key={item.id}
            className={`st-menu-item ${section === item.id ? 'active' : ''}`}
            onClick={() => setSection(item.id)}
          >
            <item.icon size={16} />
            <span>{item.label}</span>
            <ChevronRight size={13} className="st-menu-arrow" />
          </button>
        ))}
      </nav>

      {/* Right content */}
      <div className="st-content">
        {loading
          ? <div className="st-loading"><div className="st-spinner" /></div>
          : RENDERERS[section]?.()}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`st-toast st-toast-${toast.type}`}>
          {toast.type === 'success' ? '✓' : '✕'} {toast.msg}
        </div>
      )}
    </div>
  );
};

export default Settings;
